import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { createOrder } from '../api/orders';
import './OrderPage.css';

// Import material images
import acrylicMaterial from '../assets/ac.png.webp';
import aluminumMaterial from '../assets/al.webp';

const OrderPage = () => {
    const navigate = useNavigate();

    // State management
    const [selectedTemplate, setSelectedTemplate] = useState('GOLD_CLASSIC');
    const [selectedMaterial, setSelectedMaterial] = useState('ACRYLIC');
    const [selectedPlan, setSelectedPlan] = useState('BASIC');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Form data - Combined Order + Memorial + Delivery
    const [formData, setFormData] = useState({
        // Memorial data
        firstName: '',
        lastName: '',
        yearOfBirth: '',
        yearOfDeath: '',
        location: '',
        lifeStory: '',

        // Delivery data
        deliveryFirstName: '',
        deliveryLastName: '',
        deliveryCity: '',
        deliveryAddress: '',
        deliveryPostalCode: '',
        deliveryPhone: ''
    });

    // Pricing
    const pricing = {
        BASIC: 179,
        PREMIUM: 319
    };

    // Template options matching backend enum
    const templates = [
        { id: 'GOLD_CLASSIC', visualId: 'gold', label: 'Klassisches Gold' },
        { id: 'SILVER_ELEGANCE', visualId: 'silver', label: 'Silberne Eleganz' },
        { id: 'PURE_WHITE', visualId: 'white', label: 'Reinweiß' },
        { id: 'WARM_BEIGE', visualId: 'beige', label: 'Warmes Beige' },
        { id: 'DEEP_BLACK', visualId: 'black', label: 'Tiefschwarz' },
        { id: 'ICE_BLUE', visualId: 'iceblue', label: 'Eisblau' }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const firstName = formData.firstName.trim();
            const lastName = formData.lastName.trim();
            const lifeStory = formData.lifeStory.trim();

            const deliveryFirstName = formData.deliveryFirstName.trim();
            const deliveryLastName = formData.deliveryLastName.trim();
            const deliveryCity = formData.deliveryCity.trim();
            const deliveryAddress = formData.deliveryAddress.trim();

            if (!firstName || !lastName || !lifeStory) {
                throw new Error('Bitte füllen Sie alle erforderlichen Gedenkfelder aus');
            }

            if (!deliveryFirstName || !deliveryLastName || !deliveryCity || !deliveryAddress) {
                throw new Error('Bitte füllen Sie alle Lieferinformationen aus');
            }

            const yob = formData.yearOfBirth ? parseInt(formData.yearOfBirth, 10) : null;
            const yod = formData.yearOfDeath ? parseInt(formData.yearOfDeath, 10) : null;

            if (!yob || !yod) {
                throw new Error('Bitte geben Sie gültige Geburts- und Sterbejahre ein');
            }

            if (yob > yod) {
                throw new Error('Das Geburtsjahr darf nicht nach dem Sterbejahr liegen');
            }

            const orderData = {
                firstName,
                lastName,
                yearOfBirth: yob,
                yearOfDeath: yod,
                location: formData.location?.trim() || null,
                lifeStory,
                mediaUrls: [],

                templateType: selectedTemplate,
                materialType: selectedMaterial,
                planType: selectedPlan,

                deliveryFirstName,
                deliveryLastName,
                deliveryCity,
                deliveryAddress,
                deliveryPostalCode: formData.deliveryPostalCode?.trim() || null,
                deliveryPhone: formData.deliveryPhone?.trim() || null
            };

            console.log('Sending order:', orderData);

            const response = await createOrder(orderData);

            console.log('Order created:', response);

            alert('Bestellung erfolgreich erstellt!');
            navigate('/profile');
        } catch (err) {
            console.error('Error creating order:', err);

            const backendMsg =
                (typeof err?.response?.data === 'string' && err.response.data) ||
                err?.response?.data?.message;

            setError(backendMsg || err.message || 'Bestellung konnte nicht erstellt werden');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="order-page">
            {/* Background particles */}
            <div className="order-particles">
                <div className="particles-glow" />
                {[...Array(20)].map((_, index) => (
                    <div key={index} className="particle" />
                ))}
            </div>

            {/* Main Container */}
            <div className="order-container">
                <motion.h1
                    className="order-title"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    Gedenkbestellung erstellen
                </motion.h1>

                {/* Error Message */}
                {error && (
                    <motion.div
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            maxWidth: '800px',
                            margin: '0 auto 20px',
                            color: '#ff6b6b',
                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 107, 107, 0.3)',
                            textAlign: 'center'
                        }}
                    >
                        {error}
                    </motion.div>
                )}

                <div className="order-content">
                    {/* LEFT SIDE - Template & Material Selection */}
                    <motion.div
                        className="left-section"
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        {/* Template Preview */}
                        <div
                            className={`template-preview-card ${
                                templates.find((t) => t.id === selectedTemplate)?.visualId
                            }`}
                        >
                            <div className="template-header">Vorschau</div>
                            <div className="template-photo"></div>
                            <div className="template-identity">
                                <div className="template-name">Vollständiger Name</div>
                                <div className="template-dates">1950 – 2020</div>
                            </div>
                        </div>

                        {/* Template Selection */}
                        <div className="template-selector">
                            <h3 className="selector-title">Vorlage auswählen</h3>
                            <div className="template-buttons">
                                {templates.map((template) => (
                                    <motion.button
                                        key={template.id}
                                        className={`template-btn ${
                                            selectedTemplate === template.id ? 'active' : ''
                                        } ${template.visualId}`}
                                        onClick={() => setSelectedTemplate(template.id)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        disabled={loading}
                                        type="button"
                                    >
                                        {template.label}
                                    </motion.button>
                                ))}
                            </div>
                        </div>

                        {/* Material Selection */}
                        <div className="material-selector">
                            <h3 className="selector-title">Material auswählen</h3>
                            <div className="material-options">
                                <motion.div
                                    className={`material-option ${
                                        selectedMaterial === 'ACRYLIC' ? 'active' : ''
                                    }`}
                                    onClick={() => !loading && setSelectedMaterial('ACRYLIC')}
                                    whileHover={!loading ? { scale: 1.02 } : {}}
                                >
                                    <div className="material-image-container">
                                        <img src={acrylicMaterial} alt="Acryl" />
                                    </div>
                                    <div className="material-name">Acryl</div>
                                    {selectedMaterial === 'ACRYLIC' && (
                                        <div className="material-checkmark">✓</div>
                                    )}
                                </motion.div>

                                <motion.div
                                    className={`material-option ${
                                        selectedMaterial === 'ALUMINUM' ? 'active' : ''
                                    }`}
                                    onClick={() => !loading && setSelectedMaterial('ALUMINUM')}
                                    whileHover={!loading ? { scale: 1.02 } : {}}
                                >
                                    <div className="material-image-container">
                                        <img src={aluminumMaterial} alt="Aluminium" />
                                    </div>
                                    <div className="material-name">Eloxiertes Aluminium</div>
                                    {selectedMaterial === 'ALUMINUM' && (
                                        <div className="material-checkmark">✓</div>
                                    )}
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>

                    {/* RIGHT SIDE - Form */}
                    <motion.div
                        className="right-section"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <form className="order-form" onSubmit={handleSubmit}>
                            {/* Plan Selection */}
                            <div className="plan-selector">
                                <h3 className="form-section-title">Plan auswählen</h3>
                                <div className="plan-options">
                                    <label className={`plan-option ${selectedPlan === 'BASIC' ? 'active' : ''}`}>
                                        <input
                                            type="radio"
                                            value="BASIC"
                                            checked={selectedPlan === 'BASIC'}
                                            onChange={(e) => setSelectedPlan(e.target.value)}
                                            disabled={loading}
                                        />
                                        <div className="plan-content">
                                            <span className="plan-name">Basic (5 Jahre)</span>
                                            <span className="plan-price">€{pricing.BASIC}</span>
                                        </div>
                                    </label>

                                    <label
                                        className={`plan-option ${selectedPlan === 'PREMIUM' ? 'active' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            value="PREMIUM"
                                            checked={selectedPlan === 'PREMIUM'}
                                            onChange={(e) => setSelectedPlan(e.target.value)}
                                            disabled={loading}
                                        />
                                        <div className="plan-content">
                                            <span className="plan-name">Premium (10 Jahre)</span>
                                            <span className="plan-price">€{pricing.PREMIUM}</span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Memorial Information */}
                            <div className="memorial-info">
                                <h3 className="form-section-title">Gedenkinformationen</h3>

                                <div className="form-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="firstName"
                                            placeholder="Vorname*"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="lastName"
                                            placeholder="Nachname*"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <input
                                            type="number"
                                            name="yearOfBirth"
                                            placeholder="Geburtsjahr*"
                                            value={formData.yearOfBirth}
                                            onChange={handleInputChange}
                                            min="1900"
                                            max={new Date().getFullYear()}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="number"
                                            name="yearOfDeath"
                                            placeholder="Sterbejahr*"
                                            value={formData.yearOfDeath}
                                            onChange={handleInputChange}
                                            min="1900"
                                            max={new Date().getFullYear()}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="location"
                                        placeholder="Ort (z. B. Wien, Österreich)"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                    />
                                </div>

                                <div className="form-group">
                                    <textarea
                                        name="lifeStory"
                                        placeholder="Lebensgeschichte*"
                                        value={formData.lifeStory}
                                        onChange={handleInputChange}
                                        rows="4"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            {/* Delivery Information */}
                            <div className="delivery-info">
                                <h3 className="form-section-title">Lieferinformationen</h3>

                                <div className="form-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="deliveryFirstName"
                                            placeholder="Vorname*"
                                            value={formData.deliveryFirstName}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="deliveryLastName"
                                            placeholder="Nachname*"
                                            value={formData.deliveryLastName}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="deliveryCity"
                                        placeholder="Stadt*"
                                        value={formData.deliveryCity}
                                        onChange={handleInputChange}
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="deliveryAddress"
                                        placeholder="Lieferadresse*"
                                        value={formData.deliveryAddress}
                                        onChange={handleInputChange}
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="deliveryPostalCode"
                                            placeholder="Postleitzahl*"
                                            value={formData.deliveryPostalCode}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="tel"
                                            name="deliveryPhone"
                                            placeholder="Telefonnummer*"
                                            value={formData.deliveryPhone}
                                            onChange={handleInputChange}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Price Summary */}
                            <div className="price-summary">
                                <div className="price-row">
                                    <span className="price-label">Preis:</span>
                                    <span className="price-value">€{pricing[selectedPlan]}</span>
                                </div>
                                <div className="price-row total">
                                    <span className="price-label">Gesamt:</span>
                                    <span className="price-value">€{pricing[selectedPlan]}</span>
                                </div>
                                <p className="shipping-note">* Versandkosten inklusive</p>
                            </div>

                            {/* Submit Button */}
                            <motion.button
                                type="submit"
                                className="order-submit-btn"
                                whileHover={!loading ? { scale: 1.02 } : {}}
                                whileTap={!loading ? { scale: 0.98 } : {}}
                                disabled={loading}
                            >
                                {loading ? 'Wird verarbeitet…' : 'Bestellung aufgeben'}
                            </motion.button>
                        </form>
                    </motion.div>
                </div>

                {/* Back Button */}
                <motion.button
                    className="back-to-home-btn"
                    onClick={() => navigate('/')}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={loading}
                >
                    ← Zur Startseite
                </motion.button>
            </div>
        </div>
    );
};

export default OrderPage;
